import { useEditor } from "@craftjs/core";
import clsx from "clsx";
import { CSSProperties, ReactNode } from "react";
import Resizer from "../Resizer";
import Settings from "./Settings";

export type DivProps = {
    background: Record<"r" | "g" | "b" | "a", number>;
    flexDirection: string;
    alignItems: string;
    justifyContent: string;
    fillSpace: string;
    width: string;
    height: string;
    padding: string[];
    margin: string[];
    marginTop: number;
    marginLeft: number;
    marginBottom: number;
    marginRight: number;
    radius: number;
    appearance: string;
    wrap: string;
    children: ReactNode;
};

const defaultProps = {
    radius: 0,
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    fillSpace: "no",
    padding: ["0", "0", "0", "0"],
    margin: ["0", "0", "0", "0"],
    background: { r: 255, g: 255, b: 255, a: 1 },
    width: "100%",
    height: "auto",
    appearance: "flex",
    wrap: "yes",
};

const Div = (props: Partial<DivProps>) => {
    props = {
        ...defaultProps,
        ...props,
    };
    const {
        radius = 0,
        flexDirection,
        alignItems,
        justifyContent,
        fillSpace,
        background = { r: 255, g: 255, b: 255, a: 1 },
        padding = [0, 0, 0, 0],
        margin = [0, 0, 0, 0],
        appearance = "flex",
        wrap = "yes",
        children,
    } = props;

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled,
    }));

    const renderDivContent = () => {
        if (enabled && !children) {
            return (
                <div className={clsx(["flex flex-col gap-6 p-3 bg-gray-200 rounded animate-pulse"])}>
                    This is an empty container, start dropping elements here to get started!
                </div>
            );
        }

        return children;
    };

    return (
        <Resizer
            style={
                {
                    justifyContent,
                    flexDirection,
                    alignItems,
                    background: `rgba(${Object.values(background)})`,
                    padding: `${padding[0]}px ${padding[1]}px ${padding[2]}px ${padding[3]}px`,
                    margin: `${margin[0]}px ${margin[1]}px ${margin[2]}px ${margin[3]}px`,
                    flex: fillSpace === "yes" ? 1 : "unset",
                    borderRadius: `${radius}px`,
                    flexWrap: wrap === "yes" ? "wrap" : "no-wrap",
                    display: appearance,
                } as CSSProperties
            }
        >
            {renderDivContent()}
        </Resizer>
    );
};

Div.craft = {
    displayName: "Container",
    props: defaultProps,
    rules: {
        canDrag: () => true,
        canMoveOut: () => true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        canDrop: (targetNode: any) => targetNode.data.name === "PageContentContainer" || targetNode.data.name === "Div",
    },
    related: {
        settings: Settings,
    },
};

export default Div;
